import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Menu, X, User, LogOut, LayoutDashboard, Settings } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Accueil" },
    { path: "/design-durable", label: "A Propos" },
    { path: "/innovation-responsable", label: "Méthodologie" },
    { path: "/podcast", label: "Outils" },
    { path: "/strategie-rse", label: "Domaines d'Intervention" },
    { path: "/strategie-climat", label: "Ressources Utiles" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? "glass-warm shadow-lg" 
            : "bg-transparent"
        }`}
      >
        <div className="container-premium">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              data-testid="logo-link"
            >
              <div className="flex flex-col">
                <span className="font-display text-2xl font-bold tracking-tight text-[var(--charcoal)]">
                  ALMA <span className="text-[var(--terracotta)]">VIVU</span>
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                  className={`nav-link-premium ${isActive(link.path) ? "active" : ""}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Auth Section */}
            <div className="hidden lg:flex items-center gap-4">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-2 text-sm font-medium text-[var(--charcoal)] hover:text-[var(--terracotta)] transition-colors"
                      data-testid="user-menu-trigger"
                    >
                      <User className="w-4 h-4" />
                      <span>{user?.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-[var(--bone)] border-[var(--sand)]">
                    <div className="px-3 py-2 border-b border-[var(--sand)]">
                      <p className="text-sm font-medium text-[var(--charcoal)]">{user?.name}</p>
                      <p className="text-xs text-[var(--stone)]">{user?.email}</p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-link">
                        <LayoutDashboard className="w-4 h-4" />
                        Tableau de bord
                      </Link>
                    </DropdownMenuItem>
                    {user?.is_admin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin/content" className="flex items-center gap-2 cursor-pointer" data-testid="admin-content-link">
                          <Settings className="w-4 h-4" />
                          Administration
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={logout} 
                      className="flex items-center gap-2 text-[var(--terracotta)] cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/auth">
                  <button 
                    className="text-sm font-medium text-[var(--charcoal)] hover:text-[var(--terracotta)] transition-colors flex items-center gap-2"
                    data-testid="login-btn"
                  >
                    <User className="w-4 h-4" />
                    Connexion
                  </button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[var(--charcoal)]" />
              ) : (
                <Menu className="w-6 h-6 text-[var(--charcoal)]" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div 
              className="lg:hidden glass-warm border-t border-[var(--sand)] py-6 animate-fade-in"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`px-4 py-3 text-sm font-medium uppercase tracking-wider transition-colors ${
                      isActive(link.path)
                        ? "bg-[var(--sand)] text-[var(--terracotta)]"
                        : "hover:bg-[var(--sand)]"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t border-[var(--sand)] mt-4 pt-4 px-4">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/dashboard"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-2 py-3 text-sm font-medium"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Tableau de bord
                      </Link>
                      <button
                        onClick={() => {
                          logout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-2 py-3 text-sm font-medium text-[var(--terracotta)]"
                      >
                        <LogOut className="w-4 h-4" />
                        Déconnexion
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/auth"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 py-3 text-sm font-medium"
                    >
                      <User className="w-4 h-4" />
                      Connexion
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
      {/* Noise overlay */}
      <div className="noise-overlay"></div>
    </>
  );
};

export default Navbar;
